import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md",
  {
    variants: {
      variant: {
        default:
          "bg-[#F5CB2B] text-[#613F4F] hover:bg-[#F5CB2B]/90 font-semibold border-none",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "self-end border-[1px] border-black/20 flex items-center gap-2 rounded-md text-black bg-white transition-all duration-300 ease-in-out hover:bg-[#705260] disabled:bg-white/20 disabled:cursor-block hover:text-white hover:border-transparent font-medium text-base",
        secondary: "bg-[#E9E9EF] text-[##C0C2CB] pointer-events-none",
        ghost:
          "border-[1px] border-black/20 flex items-center gap-2 rounded-md text-black bg-white transition-all duration-300 ease-in-out hover:bg-[#705260] disabled:bg-white/20 disabled:cursor-block hover:text-white hover:border-transparent font-medium text-base",
        ghost_none:
          "border !border-[#DCDCE3]  hover:bg-[#B3B3B3]/90 hover:text-accent-foreground",
        ghost_none_disabled:
          "border !border-[#DCDCE3]  hover:bg-[#B3B3B3]/90 hover:text-accent-foreground pointer-events-none",
        link: "text-primary underline-offset-4 hover:underline",
        ghost_disabled: "",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && "opacity-50 cursor-not-allowed"
        )}
        ref={ref}
        disabled={isLoading}
        {...props}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-5 w-5 text-[#705260]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
